// 导入Three.js库
import * as THREE from "../../libs/three.js/build/three.module.js";
// 导入注释相关功能
import { Annotation } from "../Annotation.js";
// 导入相机模式常量
import { CameraMode } from "../defines.js";
// 导入视图组件
import { View } from "./View.js";
// 导入工具类
import { Utils } from "../utils.js";
// 导入事件调度器
import { EventDispatcher } from "../EventDispatcher.js";


/**
 * Scene类 - 管理Potree中的场景
 * 继承自EventDispatcher以支持自定义事件
 */
export class Scene extends EventDispatcher {

	/**
	 * 创建一个新的Scene实例
	 */
	constructor() {
		super();

		// 注释系统
		this.annotations = new Annotation();

		// 创建Three.js场景
		this.scene = new THREE.Scene();         // 主场景
		this.sceneBG = new THREE.Scene();       // 背景场景
		this.scenePointCloud = new THREE.Scene(); // 点云场景

		// 相机系统
		this.cameraP = new THREE.PerspectiveCamera(this.fov, 1, 0.1, 1000 * 1000);  // 透视相机
		this.cameraO = new THREE.OrthographicCamera(-1, 1, 1, -1, 0.1, 1000 * 1000); // 正交相机
		this.cameraVR = new THREE.PerspectiveCamera();                             // VR相机
		this.cameraBG = new THREE.Camera();                                        // 背景相机
		this.cameraScreenSpace = new THREE.OrthographicCamera(-1, 1, 1, -1, 0.1, 10); // 屏幕空间相机
		this.cameraMode = CameraMode.PERSPECTIVE; // 默认使用透视相机模式
		this.overrideCamera = null;               // 覆盖相机（如果有）

		// 场景内容
		this.pointclouds = [];       // 点云列表
		this.measurements = [];      // 测量工具列表
		this.profiles = [];          // 剖面工具列表
		this.volumes = [];           // 体积工具列表
		this.polygonClipVolumes = []; // 多边形裁剪体列表
		this.cameraAnimations = [];   // 相机动画列表
		this.orientedImages = [];     // 定向图像列表
		this.images360 = [];         // 360度全景图像列表
		this.geopackages = [];       // 地理包列表

		// 控制器
		this.fpControls = null;      // 第一人称控制器
		this.orbitControls = null;   // 轨道控制器
		this.earthControls = null;   // 地球控制器
		this.geoControls = null;     // 地理控制器
		this.deviceControls = null;  // 设备方向控制器
		this.inputHandler = null;    // 输入处理器

		// 视图组件
		this.view = new View();      // 当前视图

		// 光照系统
		this.directionalLight = null; // 平行光

		// 初始化场景
		this.initialize();
	}

	/**
	 * 获取可视节点的最小尺寸
	 * 用于确定点云可视性的阈值下限
	 * @returns {number} 最小节点尺寸
	 */
	getMinNodeSize() {
		let size = 0;
		this.pointclouds.forEach(p => {
			size = Math.max(size, p.material.minSize);
		});
		return size;
	}

	/**
	 * 获取可视节点的最大尺寸
	 * 用于确定点云可视性的阈值上限
	 * @returns {number} 最大节点尺寸
	 */
	getMaxNodeSize() {
		let size = 0;
		this.pointclouds.forEach(p => {
			size = Math.max(size, p.material.maxSize);
		});
		return size;
	}

	/**
	 * 估算给定位置的高度
	 * 通过射线从上方向下检测点云来估算地形高度
	 * @param {THREE.Vector3} position 需要估算高度的位置
	 * @returns {number|null} 估算的高度值，如果无法估算则返回null
	 */
	estimateHeightAt(position) {
		let height = null;
		let fromSpacing = Infinity;

		for (let pointcloud of this.pointclouds) {
			if (pointcloud.root.geometryNode === undefined) {
				continue;
			}

			let pHeight = null;
			let pFromSpacing = Infinity;

			let lpos = position.clone().sub(pointcloud.position);
			lpos.z = 0;
			let ray = new THREE.Ray(lpos, new THREE.Vector3(0, 0, 1));

			let stack = [pointcloud.root];
			while (stack.length > 0) {
				let node = stack.pop();
				let box = node.getBoundingBox();

				let inside = ray.intersectBox(box);

				if (!inside) {
					continue;
				}

				let h = node.geometryNode.mean.z +
					pointcloud.position.z +
					node.geometryNode.boundingBox.min.z;

				if (node.geometryNode.spacing <= pFromSpacing) {
					pHeight = h;
					pFromSpacing = node.geometryNode.spacing;
				}

				for (let index of Object.keys(node.children)) {
					let child = node.children[index];
					if (child.geometryNode) {
						stack.push(node.children[index]);
					}
				}
			}

			if (height === null || pFromSpacing < fromSpacing) {
				height = pHeight;
				fromSpacing = pFromSpacing;
			}
		}

		return height;
	}

	/**
	 * 获取点云的包围盒
	 * 计算所有给定点云的合并包围盒
	 * @param {Array} pointclouds 要计算包围盒的点云数组，默认为场景中的所有点云
	 * @returns {THREE.Box3} 合并后的包围盒
	 */
	getBoundingBox(pointclouds = this.pointclouds) {
		let box = new THREE.Box3();

		this.scenePointCloud.updateMatrixWorld(true);
		this.referenceFrame.updateMatrixWorld(true);

		for (let pointcloud of pointclouds) {
			pointcloud.updateMatrixWorld(true);

			let pointcloudBox = pointcloud.pcoGeometry.tightBoundingBox ? pointcloud.pcoGeometry.tightBoundingBox : pointcloud.boundingBox;
			let boxWorld = Utils.computeTransformedBoundingBox(pointcloudBox, pointcloud.matrixWorld);
			box.union(boxWorld);
		}

		return box;
	}

	/**
	 * 添加点云到场景
	 * @param {Potree.PointCloudOctree} pointcloud 要添加的点云对象
	 */
	addPointCloud(pointcloud) {
		this.pointclouds.push(pointcloud);
		this.scenePointCloud.add(pointcloud);

		this.dispatchEvent({
			type: 'pointcloud_added',
			pointcloud: pointcloud
		});
	}

	/**
	 * 从场景中移除点云
	 * @param {Potree.PointCloudOctree} pointcloud 要移除的点云对象
	 */
	removePointCloud(pointcloud) {
		let index = this.pointclouds.indexOf(pointcloud);
		if (index > -1) {
			this.pointclouds.splice(index, 1);
			this.scenePointCloud.remove(pointcloud);

			this.dispatchEvent({
				'type': 'pointcloud_removed',
				'scene': this,
				'pointcloud': pointcloud
			});
		}
	}
	/**
	 * 根据名称查找点云
	 * @param {string} name 点云名称
	 * @returns {Potree.PointCloudOctree|undefined} 找到的点云对象，若未找到则返回undefined
	 */
	findPointCloudByName(name) {
		return this.pointclouds.find(pointcloud => pointcloud.name === name);
	}

	/**
	 * 点云数据上传后的处理方法
	 * 发送点云上传事件通知
	 * @param {Object} data 上传的点云数据
	 */
	loadPointCloud(data) {
		this.dispatchEvent({
			'type': 'pointcloud_uploaded',
			'scene': this,
			'data': data
		});
	}
	// 点云处理成功之后
	handlePointCloud(data) {
		this.dispatchEvent({
			'type': 'pointcloud_handled',
			'scene': this,
			'data': data
		});
	}
	/**
	 * 添加体积测量对象到场景
	 * @param {Potree.Volume} volume 要添加的体积对象
	 */
	addVolume(volume) {
		this.volumes.push(volume);
		this.dispatchEvent({
			'type': 'volume_added',
			'scene': this,
			'volume': volume
		});
	}

	/**
	 * 添加定向图像到场景
	 * @param {Potree.OrientedImageSet} images 定向图像集
	 */
	addOrientedImages(images) {
		this.orientedImages.push(images);
		this.scene.add(images.node);

		this.dispatchEvent({
			'type': 'oriented_images_added',
			'scene': this,
			'images': images
		});
	};

	/**
	 * 从场景中移除定向图像
	 * @param {Potree.OrientedImageSet} images 要移除的定向图像集
	 */
	removeOrientedImages(images) {
		let index = this.orientedImages.indexOf(images);
		if (index > -1) {
			this.orientedImages.splice(index, 1);

			this.dispatchEvent({
				'type': 'oriented_images_removed',
				'scene': this,
				'images': images
			});
		}
	};

	/**
	 * 添加360度全景图像到场景
	 * @param {Potree.Images360} images 360度全景图像集
	 */
	add360Images(images) {
		this.images360.push(images);
		this.scene.add(images.node);

		this.dispatchEvent({
			'type': '360_images_added',
			'scene': this,
			'images': images
		});
	}

	/**
	 * 从场景中移除360度全景图像
	 * @param {Potree.Images360} images 要移除的360度全景图像集
	 */
	remove360Images(images) {
		let index = this.images360.indexOf(images);
		if (index > -1) {
			this.images360.splice(index, 1);

			this.dispatchEvent({
				'type': '360_images_removed',
				'scene': this,
				'images': images
			});
		}
	}

	/**
	 * 添加地理数据包到场景
	 * @param {Potree.Geopackage} geopackage 地理数据包对象
	 */
	addGeopackage(geopackage) {
		this.geopackages.push(geopackage);
		this.scene.add(geopackage.node);

		this.dispatchEvent({
			'type': 'geopackage_added',
			'scene': this,
			'geopackage': geopackage
		});
	};

	/**
	 * 从场景中移除地理数据包
	 * @param {Potree.Geopackage} geopackage 要移除的地理数据包
	 */
	removeGeopackage(geopackage) {
		let index = this.geopackages.indexOf(geopackage);
		if (index > -1) {
			this.geopackages.splice(index, 1);

			this.dispatchEvent({
				'type': 'geopackage_removed',
				'scene': this,
				'geopackage': geopackage
			});
		}
	};

	/**
	 * 从场景中移除体积测量对象
	 * @param {Potree.Volume} volume 要移除的体积对象
	 */
	removeVolume(volume) {
		let index = this.volumes.indexOf(volume);
		if (index > -1) {
			this.volumes.splice(index, 1);

			this.dispatchEvent({
				'type': 'volume_removed',
				'scene': this,
				'volume': volume
			});
		}
	};

	/**
	 * 添加相机动画到场景
	 * @param {Potree.CameraAnimation} animation 相机动画对象
	 */
	addCameraAnimation(animation) {
		this.cameraAnimations.push(animation);
		this.dispatchEvent({
			'type': 'camera_animation_added',
			'scene': this,
			'animation': animation
		});
	};

	/**
	 * 从场景中移除相机动画
	 * @param {Potree.CameraAnimation} animation 要移除的相机动画
	 */
	removeCameraAnimation(animation) {
		let index = this.cameraAnimations.indexOf(volume);
		if (index > -1) {
			this.cameraAnimations.splice(index, 1);

			this.dispatchEvent({
				'type': 'camera_animation_removed',
				'scene': this,
				'animation': animation
			});
		}
	};

	/**
	 * 添加多边形裁剪体积到场景
	 * @param {Potree.PolygonClipVolume} volume 多边形裁剪体积对象
	 */
	addPolygonClipVolume(volume) {
		this.polygonClipVolumes.push(volume);
		this.dispatchEvent({
			"type": "polygon_clip_volume_added",
			"scene": this,
			"volume": volume
		});
	};

	/**
	 * 从场景中移除多边形裁剪体积
	 * @param {Potree.PolygonClipVolume} volume 要移除的多边形裁剪体积
	 */
	removePolygonClipVolume(volume) {
		let index = this.polygonClipVolumes.indexOf(volume);
		if (index > -1) {
			this.polygonClipVolumes.splice(index, 1);
			this.dispatchEvent({
				"type": "polygon_clip_volume_removed",
				"scene": this,
				"volume": volume
			});
		}
	};

	/**
	 * 添加测量对象到场景
	 * @param {Potree.Measure} measurement 测量对象
	 */
	addMeasurement(measurement) {
		measurement.lengthUnit = this.lengthUnit;
		measurement.lengthUnitDisplay = this.lengthUnitDisplay;
		this.measurements.push(measurement);
		this.dispatchEvent({
			'type': 'measurement_added',
			'scene': this,
			'measurement': measurement
		});
	};

	/**
	 * 从场景中移除测量对象
	 * @param {Potree.Measure} measurement 要移除的测量对象
	 */
	removeMeasurement(measurement) {
		let index = this.measurements.indexOf(measurement);
		if (index > -1) {
			this.measurements.splice(index, 1);
			this.dispatchEvent({
				'type': 'measurement_removed',
				'scene': this,
				'measurement': measurement
			});
		}
	}

	/**
	 * 添加剖面测量对象到场景
	 * @param {Potree.Profile} profile 剖面对象
	 */
	addProfile(profile) {
		this.profiles.push(profile);
		this.dispatchEvent({
			'type': 'profile_added',
			'scene': this,
			'profile': profile
		});
	}

	/**
	 * 从场景中移除剖面测量对象
	 * @param {Potree.Profile} profile 要移除的剖面对象
	 */
	removeProfile(profile) {
		let index = this.profiles.indexOf(profile);
		if (index > -1) {
			this.profiles.splice(index, 1);
			this.dispatchEvent({
				'type': 'profile_removed',
				'scene': this,
				'profile': profile
			});
		}
	}

	/**
	 * 移除场景中所有测量对象
	 * 包括测量、剖面和体积测量对象
	 */
	removeAllMeasurements() {
		while (this.measurements.length > 0) {
			this.removeMeasurement(this.measurements[0]);
		}

		while (this.profiles.length > 0) {
			this.removeProfile(this.profiles[0]);
		}

		while (this.volumes.length > 0) {
			this.removeVolume(this.volumes[0]);
		}
	}

	/**
	 * 移除场景中所有裁剪体积
	 * 包括常规裁剪体积和多边形裁剪体积
	 */
	removeAllClipVolumes() {
		let clipVolumes = this.volumes.filter(volume => volume.clip === true);
		for (let clipVolume of clipVolumes) {
			this.removeVolume(clipVolume);
		}

		while (this.polygonClipVolumes.length > 0) {
			this.removePolygonClipVolume(this.polygonClipVolumes[0]);
		}
	}

	/**
	 * 获取当前活动相机
	 * 根据相机模式或覆盖相机返回适当的相机对象
	 * @returns {THREE.Camera} 当前活动相机
	 */
	getActiveCamera() {
		if (this.overrideCamera) {
			return this.overrideCamera;
		}

		if (this.cameraMode === CameraMode.PERSPECTIVE) {
			return this.cameraP;
		} else if (this.cameraMode === CameraMode.ORTHOGRAPHIC) {
			return this.cameraO;
		} else if (this.cameraMode === CameraMode.VR) {
			return this.cameraVR;
		}

		return null;
	}

	/**
	 * 初始化场景
	 * 设置基本组件如光照、场景组织结构等
	 */
	initialize() {

		this.referenceFrame = new THREE.Object3D();
		this.referenceFrame.matrixAutoUpdate = false;
		this.scenePointCloud.add(this.referenceFrame);

		this.cameraP.up.set(0, 0, 1);
		this.cameraP.position.set(1000, 1000, 1000);
		this.cameraO.up.set(0, 0, 1);
		this.cameraO.position.set(1000, 1000, 1000);
		//this.camera.rotation.y = -Math.PI / 4;
		//this.camera.rotation.x = -Math.PI / 6;
		this.cameraScreenSpace.lookAt(new THREE.Vector3(0, 0, 0), new THREE.Vector3(0, 0, -1), new THREE.Vector3(0, 1, 0));

		this.directionalLight = new THREE.DirectionalLight(0xffffff, 0.5);
		this.directionalLight.position.set(10, 10, 10);
		this.directionalLight.lookAt(new THREE.Vector3(0, 0, 0));
		this.scenePointCloud.add(this.directionalLight);

		let light = new THREE.AmbientLight(0x555555); // soft white light
		this.scenePointCloud.add(light);

		{ // background
			let texture = Utils.createBackgroundTexture(512, 512);

			texture.minFilter = texture.magFilter = THREE.NearestFilter;
			texture.minFilter = texture.magFilter = THREE.LinearFilter;
			let bg = new THREE.Mesh(
				new THREE.PlaneBufferGeometry(2, 2, 1),
				new THREE.MeshBasicMaterial({
					map: texture
				})
			);
			bg.material.depthTest = false;
			bg.material.depthWrite = false;
			this.sceneBG.add(bg);
		}

		// { // lights
		// 	{
		// 		let light = new THREE.DirectionalLight(0xffffff);
		// 		light.position.set(10, 10, 1);
		// 		light.target.position.set(0, 0, 0);
		// 		this.scene.add(light);
		// 	}

		// 	{
		// 		let light = new THREE.DirectionalLight(0xffffff);
		// 		light.position.set(-10, 10, 1);
		// 		light.target.position.set(0, 0, 0);
		// 		this.scene.add(light);
		// 	}

		// 	{
		// 		let light = new THREE.DirectionalLight(0xffffff);
		// 		light.position.set(0, -10, 20);
		// 		light.target.position.set(0, 0, 0);
		// 		this.scene.add(light);
		// 	}
		// }
	}

	/**
	 * 添加注释到场景
	 * @param {THREE.Vector3|Array} position 注释位置，可以是Vector3对象或坐标数组
	 * @param {Object} args 注释参数
	 * @returns {Annotation} 创建的注释对象
	 */
	addAnnotation(position, args = {}) {
		if (position instanceof Array) {
			args.position = new THREE.Vector3().fromArray(position);
		} else if (position.x != null) {
			args.position = position;
		}
		let annotation = new Annotation(args);
		this.annotations.add(annotation);

		return annotation;
	}

	/**
	 * 获取场景中的所有注释
	 * @returns {Annotation} 注释对象
	 */
	getAnnotations() {
		return this.annotations;
	};

	/**
	 * 从场景中移除指定的注释
	 * @param {Annotation} annotationToRemove 要移除的注释对象
	 */
	removeAnnotation(annotationToRemove) {
		this.annotations.remove(annotationToRemove);
	}
};
